/**
 * Whether the work-history sidebar should show its loading state.
 *
 * `loading` covers an in-flight network request. The second case covers an
 * Apollo cache-first hit: the query resolves with `loading === false` before
 * its `onCompleted` callback copies the result into the component's local
 * editable state. Without treating that gap as loading, the empty state flashes
 * for a frame before the content appears (TF-1548).
 */
export function isWorkHistoryLoading({
  loading,
  hasQueryResult,
  hasLocalData,
}: {
  loading: boolean;
  hasQueryResult: boolean;
  hasLocalData: boolean;
}): boolean {
  return loading || (hasQueryResult && !hasLocalData);
}

export enum WorkHistoryExternalObjectType {
  GITHUB_PULL_REQUEST = "github_pull_request",
  JIRA_ISSUE = "jira_issue",
  HUBSPOT_DEAL = "hubspot_deal",
  SALESFORCE_ACTIVITY = "salesforce_activity",
  LINEAR_ISSUE = "linear_issue",
  LINEAR_PROJECT = "linear_project",
  YOUTRACK_ISSUE = "youtrack_issue",
  YOUTRACK_PROJECT = "youtrack_project",
}

export const WorkHistoryExternalObjectTitles: {
  [key: string]: string;
} = {
  [WorkHistoryExternalObjectType.GITHUB_PULL_REQUEST]: "Github",
  [WorkHistoryExternalObjectType.JIRA_ISSUE]: "JIRA",
  [WorkHistoryExternalObjectType.HUBSPOT_DEAL]: "Hubspot",
  [WorkHistoryExternalObjectType.SALESFORCE_ACTIVITY]: "Salesforce",
  [WorkHistoryExternalObjectType.LINEAR_ISSUE]: "Linear",
  [WorkHistoryExternalObjectType.LINEAR_PROJECT]: "Linear",
  [WorkHistoryExternalObjectType.YOUTRACK_ISSUE]: "YouTrack",
  [WorkHistoryExternalObjectType.YOUTRACK_PROJECT]: "YouTrack",
};

export type BasicExternalEvent = {
  id: string;
  title: string;
  url: string;
  provider: string;
};

export type GithubPullRequestEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.GITHUB_PULL_REQUEST;
};
export type JiraIssueEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.JIRA_ISSUE;
};
export type HubspotDealEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.HUBSPOT_DEAL;
  status: "Open" | "Closed";
  stage: string;
  won: boolean;
  amount: number;
  engagements: { id: string; type: "email" | "call" }[];
};
export type LinearIssueEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.LINEAR_ISSUE;
};
export type LinearProjectEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.LINEAR_PROJECT;
  is_lead: boolean;
  is_member: boolean;
};
export type YouTrackIssueEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.YOUTRACK_ISSUE;
};
export type YouTrackProjectEventObject = BasicExternalEvent & {
  type: WorkHistoryExternalObjectType.YOUTRACK_PROJECT;
};

export type WorkHistoryExternalEventObject =
  | GithubPullRequestEventObject
  | JiraIssueEventObject
  | HubspotDealEventObject
  | LinearIssueEventObject
  | LinearProjectEventObject
  | YouTrackIssueEventObject
  | YouTrackProjectEventObject;
